// src/utils/smartLayer.js
// Waits for the pre-built SmartLayer bundle to finish loading
// before resolving, so no function is ever called as undefined.
//
// Supports all load cases:
//   1. window.SmartLayer already exists (sync)
//   2. window.__onSmartLayerLoaded callback fires (async)
//   3. Callback was missed — polls until window.SmartLayer appears
//   4. Bundle never loads — returns null after timeout

let _resolveReady;
const _ready = new Promise((res) => { _resolveReady = res; });

function tryResolve(sl) {
  if (sl && typeof sl === 'object') {
    _resolveReady(sl);
    return true;
  }
  return false;
}

// Case 1: Already loaded synchronously
if (window.SmartLayer) {
  tryResolve(window.SmartLayer);
} else {
  // Case 2: Register callback for when script loads
  window.__onSmartLayerLoaded = (sl) => {
    if (tryResolve(sl)) return;
  };

  // Case 3: Poll fallback in case callback was missed
  let pollAttempts = 0;
  const pollInterval = setInterval(() => {
    pollAttempts++;
    if (window.SmartLayer && tryResolve(window.SmartLayer)) {
      clearInterval(pollInterval);
      return;
    }
    if (pollAttempts >= 60) { // ~30 seconds
      clearInterval(pollInterval);
      console.error('[SmartLayer] timed out waiting for bundle to load');
      _resolveReady(null);
    }
  }, 500);
}

const _get = (name) => async (...args) => {
  const sl = await _ready;
  if (!sl) {
    console.error(`SmartLayer: bundle never loaded.`);
    return null;
  }
  if (typeof sl[name] !== 'function') {
    console.error(`SmartLayer: "${name}" is not a function. Bundle may not have loaded.`);
    return null;
  }
  return sl[name](...args);
};

export const normalizeImportedProduct         = _get('normalizeImportedProduct');
export const smartLayerFromChromeImport        = _get('smartLayerFromChromeImport');
export const SmartLayerFinalPlus               = _get('SmartLayerFinalPlus');
export const generateSmartLayerUltimateFinalSafe = _get('generateSmartLayerUltimateFinalSafe');
export const buildSmartLayerDashboardCardV2    = _get('buildSmartLayerDashboardCardV2');
export const buildSmartLayerDashboardViewModelV2 = _get('buildSmartLayerDashboardViewModelV2');
export const buildSmartLayerProductDetailPagePack = _get('buildSmartLayerProductDetailPagePack');
export const buildSmartLayerImporterPopupResponse = _get('buildSmartLayerImporterPopupResponse');
export const runSmartLayerFastPrecheck         = _get('runSmartLayerFastPrecheck');
export const executeSmartLayerSafeAction       = _get('executeSmartLayerSafeAction');
export const buildSmartLayerProductionReadinessAudit = _get('buildSmartLayerProductionReadinessAudit');
export const createSmartLayerSettings          = _get('createSmartLayerSettings');
export const smartLayerFinalDraftGate          = _get('smartLayerFinalDraftGate');
export const smartLayerSafeTry                 = _get('smartLayerSafeTry');
export const runSmartLayerCurrentSelfTest      = _get('runSmartLayerCurrentSelfTest');
export const validateSmartLayerSettings        = _get('validateSmartLayerSettings');
export const buildSmartLayerLiveListingMonitor = _get('buildSmartLayerLiveListingMonitor');
export const interpretSmartLayerMonitor        = _get('interpretSmartLayerMonitor');
export const buildSmartLayerExperimentIdeas    = _get('buildSmartLayerExperimentIdeas');
export const buildSmartLayerOrderEntryCopyPack = _get('buildSmartLayerOrderEntryCopyPack');
export const SMART_LAYER_VERSION = _ready.then(sl => sl?.SMART_LAYER_VERSION);
